import React, { useState, useEffect } from 'react';
import { obtenerPlanes } from '../../services/centroService';
import { toast } from 'react-toastify';

const SeleccionPlan = ({ onPlanSeleccionado, planSeleccionado }) => {
    const [planes, setPlanes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        cargarPlanes();
    }, []);

    const cargarPlanes = async () => {
        try {
            setLoading(true);
            const response = await obtenerPlanes();
            if (response.success && response.data && response.data.length > 0) {
                setPlanes(response.data);
            } else {
                setError('No se encontraron planes disponibles');
            }
        } catch (error) {
            console.error('Error al cargar planes:', error);
            setError('Error al cargar los planes');
            toast.error('Error al cargar los planes', {
                position: 'top-right',
                autoClose: 3000,
                style: { 
                    marginTop: '10px',
                    fontFamily: '"Exo 2", sans-serif',
                    fontWeight: 500
                }
            });
        } finally {
            setLoading(false);
        }
    };

    const handleSeleccionarPlan = (plan) => {
        onPlanSeleccionado(plan);
        toast.success(`Plan "${plan.nombre}" seleccionado`, {
            position: 'top-right',
            autoClose: 2000,
            style: { 
                marginTop: '10px',
                fontFamily: '"Exo 2", sans-serif',
                fontWeight: 500
            }
        });
    };

    // Función para renderizar la descripción con beneficios como viñetas
    const renderDescripcion = (descripcion) => {
        if (!descripcion) return null;
        
        // Buscar si hay beneficios separados por punto y coma
        const partes = descripcion.split(';');
        
        if (partes.length <= 1) {
            // No hay beneficios separados, mostrar descripción normal
            return <p>{descripcion}</p>;
        }
        
        // Separar descripción base de beneficios
        const descripcionBase = partes[0].trim();
        const beneficios = partes.slice(1).filter(beneficio => beneficio.trim());
        
        return (
            <div>
                {descripcionBase && (
                    <p className="mb-2">{descripcionBase}</p>
                )}
                {beneficios.length > 0 && (
                    <div>
                        <p className="font-medium text-gray-700 mb-1">Incluye:</p>
                        <ul className="list-disc list-inside space-y-1 text-gray-600">
                            {beneficios.map((beneficio, index) => (
                                <li key={index} className="text-xs">
                                    {beneficio.trim()}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        );
    };

    if (loading) {
        return (
            <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex justify-center items-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                    <span className="ml-3 text-gray-600">Cargando planes disponibles...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-red-800">Error</h3>
                            <div className="mt-2 text-sm text-red-700">
                                <p>{error}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (planes.length === 0) {
        return (
            <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="text-center py-8">
                    <p className="text-gray-600">No hay planes disponibles</p>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-xl font-semibold text-gray-800 mb-6 text-center">
                Seleccione un Plan
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {planes.map((plan) => (
                    <div 
                        key={plan.id_plan} 
                        className={`border rounded-lg p-4 cursor-pointer transition-all duration-200 ${
                            planSeleccionado?.id_plan === plan.id_plan 
                                ? 'border-blue-500 bg-blue-50 shadow-md' 
                                : 'border-gray-200 hover:border-blue-300 hover:shadow-sm'
                        }`}
                        onClick={() => handleSeleccionarPlan(plan)}
                    >
                        <div className="text-center">
                            <h4 className="text-lg font-semibold text-gray-800 mb-2">
                                {plan.nombre}
                            </h4>
                            <p className="text-2xl font-bold text-blue-600 mb-3">
                                ${plan.monto}
                            </p>
                            {plan.descripcion && (
                                <div className="text-gray-600 text-sm mb-3">
                                    {renderDescripcion(plan.descripcion)}
                                </div>
                            )}
                            <div className="text-sm text-gray-500">
                                <p><strong>Horario:</strong></p>
                                <p>{plan.hora_inicio} - {plan.hora_fin}</p>
                            </div>
                            {planSeleccionado?.id_plan === plan.id_plan && (
                                <div className="mt-3">
                                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                        <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                        Seleccionado
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>
            
            {planSeleccionado && (
                <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-md">
                    <div className="flex items-center">
                        <svg className="h-5 w-5 text-green-400 mr-2" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span className="text-green-800 font-medium">
                            Plan "{planSeleccionado.nombre}" seleccionado - ${planSeleccionado.monto}
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SeleccionPlan;